import { MRT_RowData, MRT_TableInstance } from 'material-react-table'
import * as XLSX from 'xlsx'
import { formatDate, formatDateTime, formatTime } from './tom-table.utils'

export interface IXlsxExportColumnConfig {
  header?: string
  type?: 'string' | 'number' | 'boolean' | 'date'
  hide?: boolean
}

export type IDataExportConfig<T> = Partial<Record<keyof T, IXlsxExportColumnConfig>>

export const exportTableToXlsx = <T extends MRT_RowData>(
  workbookTitle: string,
  table: MRT_TableInstance<T>,
  config?: IDataExportConfig<T>,
  fileName?: string | undefined,
) => {
  const selectedRows = table.getSelectedRowModel().rows.map(row => row.original)

  exportToXlsx(workbookTitle, selectedRows, config, fileName)
}

export const exportTableToCsv = <T extends MRT_RowData>(
  workbookTitle: string,
  table: MRT_TableInstance<T>,
  config?: IDataExportConfig<T>,
  fileName?: string,
) => {
  const selectedRows = table.getSelectedRowModel().rows.map(row => row.original)

  exportToCsv(workbookTitle, selectedRows, config, fileName)
}

export const exportToXlsx = <T extends object>(
  workbookTitle: string,
  rows: T[],
  config?: IDataExportConfig<T>,
  fileName?: string,
) => {
  const workbook = createWorkbook(workbookTitle, rows, config)

  if (!workbook) return

  XLSX.writeFile(workbook, fileName ?? generateFileName(workbookTitle, 'xlsx'), {
    compression: true,
  })
}

export const exportToCsv = <T extends object>(
  workbookTitle: string,
  rows: T[],
  config?: IDataExportConfig<T>,
  fileName?: string,
  createFromConfig = false,
) => {
  const workbook =
    config && createFromConfig
      ? createWorkbookFromConfig(workbookTitle, rows, config)
      : createWorkbook(workbookTitle, rows, config)

  if (!workbook) return

  XLSX.writeFile(workbook, fileName ?? generateFileName(workbookTitle, 'csv'), {
    bookType: 'csv',
  })
}

// [VesselVisitOverview] TODO: UT
export const createWorkbook = <T extends object>(
  workSheetName: string,
  rows: T[],
  config?: IDataExportConfig<T>,
) => {
  if (!rows.length) return
  // Excel sheet names are strictly limited to 31 characters and cannot contain special chars (:/\|?*[]).
  // Truncating to prevent application crash.
  const safeSheetName = workSheetName.slice(0, 31).replace(/[:\\/?*[\]]/g, '')

  const keys = Object.keys(rows[0]).filter(key => !config?.[key as keyof T]?.hide)
  const workSheetData = config ? rows.map(row => applyTypeConfiguration(row, config)) : rows

  const worksheet = XLSX.utils.json_to_sheet(workSheetData)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName)

  applyHeaderConfiguration(keys, worksheet, config)
  setColumnWidths(keys, workSheetData, worksheet)

  return workbook
}

export const createWorkbookFromConfig = <T extends object>(
  workSheetName: string,
  rows: T[],
  config: IDataExportConfig<T>,
) => {
  if (!rows.length) return

  // Excel sheet names are strictly limited to 31 characters and cannot contain special chars (:/\|?*[]).
  // Truncating to prevent application crash.
  const safeSheetName = workSheetName.slice(0, 31).replace(/[:\\/?*[\]]/g, '')

  const keys = Object.keys(config)
  const columnNames = keys.filter(key => !config?.[key as keyof T]?.hide)

  const allKeys = Object.values(rows.map(row => Object.keys(row))).flat()
  const uniqueAllKeys = Array.from(new Set(allKeys))

  uniqueAllKeys.forEach(key => {
    if (!config?.[key as keyof T]) {
      config = {
        ...config,
        [key]: { hide: true },
      }
    }
  })

  const workSheetData = config ? rows.map(row => applyTypeConfiguration(row, config)) : rows

  const worksheet = XLSX.utils.json_to_sheet(workSheetData)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName)

  applyHeaderConfiguration(columnNames, worksheet, config)
  setColumnWidths(columnNames, workSheetData, worksheet)

  return workbook
}

export const exportMultipleCsvFiles = <T1 extends object, T2 extends object>(
  baseFileName: string,
  table1Data: T1[],
  table1Config: IDataExportConfig<T1>,
  table2Data: T2[],
  table2Config: IDataExportConfig<T2>,
  suffix1: string,
  suffix2: string,
) => {
  const downloadSingleCsv = <T extends object>(
    data: T[],
    config: IDataExportConfig<T>,
    suffix: string,
  ) => {
    if (!data.length) return

    const keys = Object.keys(config) as (keyof T)[]
    const headers = keys
      .filter(key => !config[key]?.hide)
      .map(key => config[key]?.header ?? String(key))

    const rows = data.map(row => {
      const formattedRow = applyTypeConfiguration(row, config)
      return keys.filter(key => !config[key]?.hide).map(key => formattedRow[key] ?? '')
    })

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows])
    const workbook = XLSX.utils.book_new()

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')

    const fileName = generateFileNameWithDate(`${baseFileName}${suffix}`, 'csv')

    XLSX.writeFile(workbook, fileName, { bookType: 'csv' })
  }

  if (table1Data.length) {
    downloadSingleCsv(table1Data, table1Config, suffix1)
  }

  if (table2Data.length) {
    setTimeout(() => {
      downloadSingleCsv(table2Data, table2Config, suffix2)
    }, 300)
  }
}

export const exportDualSheetToXlsx = <T1 extends object, T2 extends object>(
  workbookTitle: string,
  table1Data: T1[],
  table1Config: IDataExportConfig<T1>,
  table2Data: T2[],
  table2Config: IDataExportConfig<T2>,
  sheet1Title: string,
  sheet2Title: string,
  fileName?: string,
) => {
  const workbook = XLSX.utils.book_new()

  const appendSheet = <T extends object>(
    data: T[],
    config: IDataExportConfig<T>,
    sheetName: string,
  ) => {
    if (!data.length) return
    const safeSheetName = sheetName.slice(0, 31).replace(/[:\\/?*[\]]/g, '')

    const keys = Object.keys(config).filter(key => !config[key as keyof T]?.hide)

    const cleanedData = data.map(row => {
      const formatted = applyTypeConfiguration(row, config)
      const finalRow: any = {}
      keys.forEach(k => {
        finalRow[k] = (formatted as any)[k] ?? ''
      })
      return finalRow
    })

    const worksheet = XLSX.utils.json_to_sheet(cleanedData, { header: keys })

    applyHeaderConfiguration(keys, worksheet, config)
    setColumnWidths(keys, cleanedData, worksheet)

    XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName)
  }

  appendSheet(table1Data, table1Config, sheet1Title)
  appendSheet(table2Data, table2Config, sheet2Title)

  XLSX.writeFile(workbook, fileName ?? generateFileNameWithDate(workbookTitle, 'xlsx'), {
    compression: true,
  })
}

const applyTypeConfiguration = <T>(dto: T, config: IDataExportConfig<T>): T => {
  const keys = Object.keys(config) as (keyof T)[]
  let result = { ...dto }

  if (!keys.length) return dto

  keys.forEach(key => {
    const columnConfig = config[key]
    const keyOfT = key as keyof T

    if (columnConfig?.hide) delete result[keyOfT]

    if (columnConfig?.type === 'date') {
      const dateValue = dto[keyOfT]
      if (dateValue && typeof result[keyOfT] === 'string') {
        result = {
          ...result,
          [keyOfT]: formatDateTime(dateValue),
        }
      }
    }
    // Handle more data types here
  })

  return result
}

const calculateMaxWidths = <T extends object>(keys: string[], workSheetData: T[]) => {
  return keys.map(key => {
    const maxValueLength = Math.max(
      ...workSheetData.map(row => String(row[key as keyof T] ?? '').length),
    )
    return Math.max(maxValueLength, key.length)
  })
}

const applyHeaderConfiguration = <T extends object>(
  keys: string[],
  worksheet: XLSX.WorkSheet,
  config?: IDataExportConfig<T>,
) => {
  const headerConfig = keys
    .map(key => {
      if (config?.[key as keyof T]?.hide) return undefined

      const headerName = config?.[key as keyof T]?.header ?? key
      return headerName
    })
    .filter(headerName => headerName)
    .map(headerName => headerName!)

  XLSX.utils.sheet_add_aoa(worksheet, [headerConfig], { origin: 'A1' })
}

export const generateFileName = (title: string, extension: string) => {
  return `${title.replace(/\s+/g, '_')}.${extension}`
}

export const generateFileNameWithDate = (filename: string, extension: string) => {
  const date = new Date()
  const formattedDate = formatDate(date)?.replaceAll('/', '-')
  const formattedTime = formatTime(date)?.replaceAll(':', '-')
  return `${filename.replace(/\s+/g, '_')}_${formattedDate}_${formattedTime}.${extension}`
}

const setColumnWidths = <T extends object>(
  keys: string[],
  workSheetData: T[],
  worksheet: XLSX.WorkSheet,
) => {
  const valuesMaxWidths = calculateMaxWidths(keys, workSheetData)
  worksheet['!cols'] = valuesMaxWidths.map(width => ({ wch: width }))
}
